<script setup lang="ts">
import { ref, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePageLoaderStore } from '@/stores/pageLoader'

const { t } = useI18n()
const loader = usePageLoaderStore()

const visible = ref(false)
const fadingOut = ref(false)

watch(() => loader.active, (isActive) => {
  if (isActive) {
    visible.value = true
    fadingOut.value = false
    // Dismiss the global app-loader if it's still showing
    document.getElementById('app-loader')?.remove()
  } else if (visible.value) {
    // Start fade-out, then remove after transition completes
    fadingOut.value = true
  }
})

function handleTransitionEnd(e: TransitionEvent) {
  // Only react to the opacity transition on the overlay itself
  if (e.propertyName === 'opacity' && fadingOut.value) {
    visible.value = false
    fadingOut.value = false
  }
}

onUnmounted(() => {
  if (loader.active) loader.finish()
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="page-loader-overlay"
      :class="{ 'is-fading-out': fadingOut }"
      @transitionend="handleTransitionEnd"
    >
      <div class="page-loader-content">
        <svg class="page-loader-logo" width="50" viewBox="0 -50 168 100" xmlns="http://www.w3.org/2000/svg">
          <rect class="pj-loader-bar pj-lb-1" x="0"     y="-30" width="16.6" height="60" rx="8.3" fill="#001F30" />
          <rect class="pj-loader-bar pj-lb-2" x="27.2"  y="-30" width="22.1" height="60" rx="11"  fill="#00A7FF" />
          <rect class="pj-loader-bar pj-lb-3" x="61"    y="-30" width="16.6" height="60" rx="8.3" fill="#001F30" />
          <rect class="pj-loader-bar pj-lb-4" x="90.9"  y="-30" width="16.6" height="60" rx="8.3" fill="#001F30" />
          <rect class="pj-loader-bar pj-lb-5" x="121.4" y="-30" width="16.6" height="60" rx="8.3" fill="#001F30" />
          <rect class="pj-loader-bar pj-lb-6" x="151.4" y="-30" width="16.6" height="60" rx="8.3" fill="#001F30" />
        </svg>
        <!-- Creation hands directly into the editor's boot phase. Keep both
             logo-only so copy does not jump between two transient states. -->
        <p v-if="loader.phase !== 'creating' && loader.phase !== 'loading-builder'" class="page-loader-text">
          {{ t(loader.messageKey) }}
        </p>
        <Transition name="page-loader-sub" mode="out-in">
          <p
            v-if="loader.phase !== 'creating' && loader.phase !== 'loading-builder' && loader.subMessageKey"
            :key="loader.subMessageKey"
            class="page-loader-subtext"
          >
            {{ t(loader.subMessageKey) }}
          </p>
        </Transition>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.page-loader-overlay {
  position: fixed;
  inset: 0;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100000;
  opacity: 1;
  transition: opacity 0.4s ease;
}

.page-loader-overlay.is-fading-out {
  opacity: 0;
}

.page-loader-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.page-loader-logo {
  width: 50px;
  height: auto;
}

.page-loader-text {
  font-size: 16px;
  font-weight: 600;
  color: #001F30;
  margin: 0;
}

.page-loader-subtext {
  font-size: 14px;
  color: rgba(0, 31, 48, 0.6);
  margin: 0;
}

/* Sub-message crossfade */
.page-loader-sub-enter-active,
.page-loader-sub-leave-active {
  transition: opacity 0.3s ease;
}
.page-loader-sub-enter-from,
.page-loader-sub-leave-to {
  opacity: 0;
}
</style>
